import { cn, Label, RadioGroup, RadioGroupItem, Textarea } from '@commander/ui';
import { RiInformationLine } from '@remixicon/react';
import type React from 'react';
import type { CommunicationStyle } from '../../types/onboarding.types';
import { COMMUNICATION_STYLES } from '../../utils/constants';

interface CommunicationStyleSelectorProps {
  selectedStyle: CommunicationStyle;
  customStyleText: string;
  onStyleChange: (style: CommunicationStyle) => void;
  onCustomStyleTextChange: (text: string) => void;
  className?: string;
}

const CommunicationStyleSelector: React.FC<CommunicationStyleSelectorProps> = ({
  selectedStyle,
  customStyleText,
  onStyleChange,
  onCustomStyleTextChange,
  className,
}) => {
  const TooltipContent: React.FC<{ data: (typeof COMMUNICATION_STYLES)[0] }> = ({ data }) => (
    <div className="font-paragraph rounded-xl">
      <div className="flex items-center justify-start mb-2">
        <span className="bg-[#F8F9FA] p-1 rounded-md inline-block">
          <RiInformationLine className="w-4 h-4" />
        </span>
      </div>
      <div className="px-2">
        {data.description.map((text, index) => (
          <p
            key={`${data.style}-desc-${index}`}
            className={cn('text-left text-xs', index === 0 ? 'mt-2' : '')}
          >
            {text}
          </p>
        ))}
      </div>
      {data.example && (
        <div className="flex flex-col gap-4 text-left bg-[#C3DBD9] p-2 rounded-xl mt-3">
          <div className="text-left w-full">
            <p className="font-bold text-xs text-[#08736D] font-paragraph">Example</p>
            <p className="text-xs">{data.example.greeting}</p>
          </div>
          <div className="text-left flex flex-col gap-2">
            {data.example.paragraphs.map((paragraph, index) => (
              <p key={`${data.style}-para-${index}`} className="text-xs">
                {paragraph}
              </p>
            ))}
          </div>
          <div className="text-xs leading-tight">
            <p>
              Thanks,
              <br />
              {data.example.closing}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={cn('space-y-4', className)}>
      <Label className="text-base font-medium text-[#737982]">Communication Style</Label>

      <RadioGroup
        value={selectedStyle}
        onValueChange={(value) => onStyleChange(value as CommunicationStyle)}
      >
        {COMMUNICATION_STYLES.map((styleData) => (
          <div key={styleData.style} className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value={styleData.style.toUpperCase()}
                id={styleData.style}
                className="text-[#08736D]"
              />
              <Label
                htmlFor={styleData.style}
                className="font-medium cursor-pointer text-[#737982]"
              >
                {styleData.style}
              </Label>

              <div className="relative group">
                <span className="cursor-pointer">
                  <span className="text-gray-400 hover:text-gray-600 transition-colors">
                    <RiInformationLine className="w-4 h-4" />
                  </span>
                </span>

                {/* Tooltip */}
                <div className="absolute ml-4 transform -translate-x-1/2 top-0 right-0 text-wrap mt-2 w-[285px] bg-white cursor-pointer p-2 text-xs text-gray-700 rounded shadow-lg font-paragraph opacity-0 invisible group-hover:opacity-100 group-hover:visible z-20 flex items-center border border-gray-200">
                  <TooltipContent data={styleData} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </RadioGroup>

      {/* Custom style textarea */}
      {selectedStyle === 'OTHER' && (
        <div className="mt-4 ml-6">
          <Label htmlFor="customStyle" className="block text-sm font-medium mb-2 text-[#000]">
            Custom Email Template
          </Label>
          <Textarea
            id="customStyle"
            value={customStyleText}
            onChange={(e) => onCustomStyleTextChange(e.target.value)}
            placeholder="Provide a sample email you'd like Commander to model after..."
            className="min-h-[120px] w-full max-w-[508px] bg-transparent border border-border-gray focus:border-0 focus:ring-[#08736D] rounded-lg p-2 text-xs text-[#737982] placeholder:text-[#AEBECD] resize-none dark:focus:ring-0 dark:focus:border-0"
          />
        </div>
      )}
    </div>
  );
};

export default CommunicationStyleSelector;
